import { useEffect } from 'react';
import {
    Box,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    IconButton,
    FormHelperText,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import {
    formLabelStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import { TextEditor } from '../../../../components';
import { useTriggerForm } from '../new/useTriggerForm.js';
import walkthroughIds from '../walkthroughIds';

const ActionHeaders = ({ setCursorPosition, darkMode }) => {
    const { editMode } = walkthroughIds;

    const { formData, headers, setHeaders } = useTriggerForm();

    const parseActionHeaders = (headersObj) => {
        if (Array.isArray(headersObj)) {
            return headersObj.length > 0
                ? headersObj
                : [{ id: 1, key: '', value: '' }];
        }

        if (headersObj && typeof headersObj === 'object') {
            const headerEntries = Object.entries(headersObj);
            if (headerEntries.length === 0) {
                return [{ id: 1, key: '', value: '' }];
            }

            return headerEntries.map(([key, value], index) => ({
                id: index + 1,
                key: key,
                value: value,
            }));
        }

        return [{ id: 1, key: '', value: '' }];
    };

    useEffect(() => {
        if (headers) {
            const parsedHeaders = parseActionHeaders(headers);
            if (JSON.stringify(headers) !== JSON.stringify(parsedHeaders)) {
                setHeaders(parsedHeaders);
            }
        } else if (formData?.action?.headers) {
            const parsedHeaders = parseActionHeaders(formData.action.headers);
            setHeaders(parsedHeaders);
        } else {
            setHeaders([{ id: 1, key: '', value: '' }]);
        }
    }, [headers, setHeaders, formData?.action?.headers]);

    const getTableState = () => {
        const headersArray = headers || [];
        if (!Array.isArray(headersArray) || headersArray.length === 0) {
            return [{ id: 1, key: '', value: '' }];
        }
        return headersArray;
    };

    const updateHeaders = (newHeaders) => {
        const validHeaders = Array.isArray(newHeaders) ? newHeaders : [];
        setHeaders(validHeaders);
    };

    const handleKeyChange = (e, id, value, index) => {
        const newPair = {
            key: e.target.value,
            id,
            value,
        };
        const currentHeaders = getTableState();
        const newTable = [...currentHeaders];
        newTable[index] = newPair;
        updateHeaders(newTable);
    };

    const handleValueChange = (e, id, key, index) => {
        const newPair = {
            value: e.target.value,
            id,
            key,
        };
        const currentHeaders = getTableState();
        const newTable = [...currentHeaders];
        newTable[index] = newPair;
        updateHeaders(newTable);
    };

    const handleKeyPress = (e, id, index) => {
        if (e.key === 'Enter' && index === getTableState().length - 1) {
            e.preventDefault();
            const currentHeaders = getTableState();
            const newId = Math.max(...currentHeaders.map((h) => h.id || 0)) + 1;
            const newHeaders = [
                ...currentHeaders,
                {
                    id: newId,
                    key: '',
                    value: '',
                },
            ];
            updateHeaders(newHeaders);
        }
    };

    const handleDeleteRow = (index) => {
        const currentHeaders = getTableState();
        const newTable = [...currentHeaders];
        newTable.splice(index, 1);

        if (newTable.length === 0) {
            newTable.push({ id: 1, key: '', value: '' });
        }

        updateHeaders(newTable);
    };

    const currentHeaders = getTableState();
    return (
        <>
            <Box marginBottom={4}>
                <Text sx={formLabelStyling(darkMode)}>Headers</Text>
                <Text sx={descriptionStyling} maxWidth={250}>
                    Add custom headers for the webhook request.
                </Text>
            </Box>
            <TableContainer>
                <Table variant="simple" size="sm">
                    <Thead>
                        <Tr>
                            <Th textTransform="none">Key</Th>
                            <Th textTransform="none">Value</Th>
                            <Th width="50px"></Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {Array.isArray(currentHeaders) &&
                            currentHeaders
                                .sort((left, right) => {
                                    return (left?.id || 0) - (right?.id || 0);
                                })
                                .map(({ key, value, id }, index) => (
                                    <Tr key={`header-${id}-${index}`}>
                                        <Td>
                                            <TextEditor
                                                id={`webHookHeaderKey-${id}`}
                                                field="key"
                                                setCursorPosition={
                                                    setCursorPosition
                                                }
                                                textfield={{
                                                    value: key || '',
                                                    multiline: false,
                                                    onChange: (e) =>
                                                        handleKeyChange(
                                                            e,
                                                            id,
                                                            value,
                                                            index,
                                                        ),
                                                    onKeyPress: (e) =>
                                                        handleKeyPress(
                                                            e,
                                                            id,
                                                            index,
                                                        ),
                                                }}
                                                dataWalkthroughId={
                                                    index === 0
                                                        ? editMode.ufHeadersKeyInput
                                                        : null
                                                }
                                            />
                                        </Td>
                                        <Td>
                                            <TextEditor
                                                id={`webHookHeaderValue-${id}`}
                                                field="value"
                                                setCursorPosition={
                                                    setCursorPosition
                                                }
                                                textfield={{
                                                    value: value || '',
                                                    multiline: false,
                                                    onChange: (e) =>
                                                        handleValueChange(
                                                            e,
                                                            id,
                                                            key,
                                                            index,
                                                        ),
                                                    onKeyPress: (e) =>
                                                        handleKeyPress(
                                                            e,
                                                            id,
                                                            index,
                                                        ),
                                                }}
                                                dataWalkthroughId={
                                                    index === 0
                                                        ? editMode.ufHeadersValueInput
                                                        : null
                                                }
                                            />
                                        </Td>
                                        <Td>
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                height="100%">
                                                {currentHeaders.length > 1 && (
                                                    <IconButton
                                                        aria-label="Delete header"
                                                        icon={<CloseIcon />}
                                                        size="sm"
                                                        variant="ghost"
                                                        colorScheme="gray"
                                                        onClick={() =>
                                                            handleDeleteRow(
                                                                index,
                                                            )
                                                        }
                                                        data-walkthroughid={
                                                            editMode.ufClearHeadersBtn
                                                        }
                                                    />
                                                )}
                                            </Box>
                                        </Td>
                                    </Tr>
                                ))}
                    </Tbody>
                </Table>
            </TableContainer>
            <FormHelperText>
                The body will be submitted with header: &quot;Content-Type:
                application/json&quot;. Press Enter to add a new row.
            </FormHelperText>
        </>
    );
};

export default ActionHeaders;
